<template>
  <div class="w-64 h-full pt-8 bg-gray-800 flex flex-col">
    <RouterLink
      v-if="account.logo"
      :to="{ name: 'accounts.show', params: { accountId: account.id } }"
    >
      <img :src="account.logo" class="block rounded w-24 mb-8 mx-auto" />
    </RouterLink>
    <h6 class="text-center text-white mb-6">
      {{ account.name }}
    </h6>
    <ul class="nav nav--dark overflow-y-auto flex flex-col flex-1 pb-2">
      <RouterLink
        :to="{ name: 'accounts.show', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
        exact
      >
        <a class="nav__link capitalize">{{ $t('account.sidebar.home') }}</a>
      </RouterLink>
      <RouterLink
        v-if="$membershipHasPermissionTo('read projects')"
        :to="{ name: 'accounts.projects', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize flex justify-between items-baseline">
          {{ $t('account.sidebar.myProjects') }}

          <span
            v-if="$currentAccount.projectsCount"
            class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
          >
            {{ $currentAccount.projectsCount }}
          </span>
        </a>
      </RouterLink>
      <RouterLink
        v-if="$membershipHasPermissionTo('read contacts')"
        :to="{ name: 'accounts.accountId.contacts', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize flex justify-between items-baseline">
          {{ $t('account.sidebar.myContacts') }}

          <span
            v-if="$currentAccount.contactsCount"
            class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
          >
            {{ $currentAccount.contactsCount }}
          </span>
        </a>
      </RouterLink>
      <RouterLink
        v-if="$membershipHasPermissionTo('read contact requests')"
        :to="{ name: 'accounts.accountId.contactRequests', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize flex justify-between items-baseline">
          {{ $t('account.sidebar.myContactRequests') }}

          <span
            v-if="$currentAccount.contactRequestsCount"
            class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
          >
            {{ $currentAccount.contactRequestsCount }}
          </span>
        </a>
      </RouterLink>
      <RouterLink
        v-if="$membershipHasPermissionTo('read pipelines')"
        :to="{ name: 'accounts.pipelines', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize flex justify-between items-baseline">
          {{ $t('account.sidebar.myPipelines') }}

          <span
            v-if="$currentAccount.pipelinesCount"
            class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
          >
            {{ $currentAccount.pipelinesCount }}
          </span>
        </a>
      </RouterLink>
      <RouterLink
        v-if="$membershipHasPermissionTo('read clients') && account.type === 'organization'"
        :to="{ name: 'accounts.accountId.clients', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize flex justify-between items-baseline">
          {{ $t('account.sidebar.myClients') }}

          <span
            v-if="$currentAccount.clientsCount"
            class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
          >
            {{ $currentAccount.clientsCount }}
          </span>
        </a>
      </RouterLink>
      <RouterLink
        v-if="$membershipHasPermissionTo('read providers') && account.type === 'organization'"
        :to="{ name: 'accounts.accountId.providers', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize flex justify-between items-baseline">
          {{ $t('account.sidebar.myProviders') }}

          <span
            v-if="$currentAccount.providersCount"
            class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
          >
            {{ $currentAccount.providersCount }}
          </span>
        </a>
      </RouterLink>
      <RouterLink
        v-if="$membershipHasPermissionTo('read agencies') && account.type === 'client'"
        :to="{ name: 'accounts.accountId.agencies', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize flex justify-between items-baseline">
          {{ $t('account.sidebar.myAgencies') }}

          <span
            v-if="$currentAccount.agenciesCount"
            class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
          >
            {{ $currentAccount.agenciesCount }}
          </span>
        </a>
      </RouterLink>
      <RouterLink
        v-if="$membershipHasPermissionTo('read assets')"
        :to="{ name: 'accounts.accountId.assets', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize flex justify-between items-baseline">
          {{ $t('account.sidebar.myAssets') }}

          <span
            v-if="$currentAccount.assetsCount"
            class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
          >
            {{ $currentAccount.assetsCount }}
          </span>
        </a>
      </RouterLink>
      <RouterLink
        v-if="
          $isAdminOrSuperAdmin ||
          ($currentAccount.type === 'provider' && $membershipHasPermissionTo('read tasks'))
        "
        :to="{ name: 'accounts.accountId.tasks', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize">{{ $t('account.sidebar.tasks') }}</a>
      </RouterLink>
      <RouterLink
        v-if="$currentAccount.type === 'client' && $membershipHasPermissionTo('read offers')"
        :to="{ name: 'accounts.accountId.offers', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize">{{ $t('account.sidebar.offers') }}</a>
      </RouterLink>
      <RouterLink
        v-if="$currentAccount.type === 'client' && $membershipHasPermissionTo('read invoices')"
        :to="{ name: 'accounts.accountId.invoices', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize">{{ $t('account.sidebar.invoices') }}</a>
      </RouterLink>
      <RouterLink
        :to="{ name: 'accounts.calendar', params: { accountId: account.id } }"
        tag="li"
        class="nav__item"
      >
        <a class="nav__link capitalize">{{ $t('account.sidebar.calendar') }}</a>
      </RouterLink>

      <a role="button" class="mt-6 mb-2 block" @click="showMore = !showMore">
        <h6 class="nav__section text-sm px-4 uppercase text-gray-600 flex justify-between">
          {{ $t(`account.sidebar.${showMore ? 'less' : 'more'}`) }}

          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            class="fill-current"
          >
            <path
              v-if="showMore"
              d="M6.71 14.71C6.80251 14.8027 6.9124 14.8762 7.03338 14.9264C7.15435
              14.9766 7.28403 15.0024 7.415 15.0024C7.54597 15.0024 7.67565 14.9766
              7.79662 14.9264C7.9176 14.8762 8.02749 14.8027 8.12 14.71L12 10.83L15.88
              14.71C15.9726 14.8026 16.0825 14.876 16.2035 14.9261C16.3244 14.9762
              16.4541 15.002 16.585 15.002C16.7159 15.002 16.8456 14.9762 16.9665
              14.9261C17.0875 14.876 17.1974 14.8026 17.29 14.71C17.3826 14.6174
              17.456 14.5075 17.5061 14.3865C17.5562 14.2656 17.582 14.1359 17.582
              14.005C17.582 13.8741 17.5562 13.7444 17.5061 13.6235C17.456 13.5025
              17.3826 13.3926 17.29 13.3L12.7 8.71C12.6075 8.61729 12.4976 8.54375
              12.3766 8.49356C12.2557 8.44338 12.126 8.41755 11.995 8.41755C11.864
              8.41755 11.7343 8.44338 11.6134 8.49356C11.4924 8.54375 11.3825 8.61729
              11.29 8.71L6.7 13.3C6.32 13.68 6.32 14.32 6.71 14.71Z"
            />
            <path
              v-else
              d="M17.29 9.29C17.1975 9.1973 17.0876 9.12375 16.9666 9.07357C16.8457
              9.02339 16.716 8.99756 16.585 8.99756C16.454 8.99756 16.3243 9.02339
              16.2034 9.07357C16.0824 9.12375 15.9725 9.1973 15.88 9.29L12 13.17L8.12
              9.29C8.02742 9.19742 7.91751 9.12398 7.79654 9.07388C7.67558 9.02377
              7.54593 8.99798 7.415 8.99798C7.28407 8.99798 7.15442 9.02377 7.03346
              9.07388C6.91249 9.12398 6.80258 9.19742 6.71 9.29C6.61742 9.38258
              6.54398 9.4925 6.49387 9.61346C6.44377 9.73442 6.41798 9.86407 6.41798
              9.995C6.41798 10.1259 6.44377 10.2556 6.49387 10.3765C6.54398 10.4975
              6.61742 10.6074 6.71 10.7L11.3 15.29C11.3925 15.3827 11.5024 15.4563 11.6234
              15.5064C11.7444 15.5566 11.874 15.5824 12.005 15.5824C12.136 15.5824
              12.2657 15.5566 12.3866 15.5064C12.5076 15.4563 12.6175 15.3827 12.71
              15.29L17.3 10.7C17.68 10.32 17.68 9.68 17.29 9.29Z"
            />
          </svg>
        </h6>
      </a>

      <template v-if="showMore">
        <RouterLink
          v-if="$membershipHasPermissionTo('read account subscriptions')"
          :to="{ name: 'accounts.accountSubscriptions', params: { accountId: account.id } }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize flex justify-between items-baseline">
            {{ $t('account.sidebar.mySubscriptions') }}

            <span
              v-if="$currentAccount.accountSubscriptionsCount"
              class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
            >
              {{ $currentAccount.accountSubscriptionsCount }}
            </span>
          </a>
        </RouterLink>
        <RouterLink
          v-if="$membershipHasPermissionTo('read pages')"
          :to="{ name: 'accounts.pages', params: { accountId: account.id } }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize flex justify-between items-baseline">
            {{ $t('account.sidebar.pages') }}

            <span
              v-if="$currentAccount.pagesCount"
              class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
            >
              {{ $currentAccount.pagesCount }}
            </span>
          </a>
        </RouterLink>
        <RouterLink
          v-if="$membershipHasPermissionTo('read channels')"
          :to="{ name: 'accounts.channels', params: { accountId: account.id } }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize flex justify-between items-baseline">
            {{ $t('account.sidebar.ads') }}

            <span
              v-if="$currentAccount.channelsCount"
              class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
            >
              {{ $currentAccount.channelsCount }}
            </span>
          </a>
        </RouterLink>
        <RouterLink
          v-if="$membershipHasPermissionTo('read contact lists')"
          :to="{ name: 'accounts.contactLists', params: { accountId: account.id } }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize flex justify-between items-baseline">
            {{ $t('account.sidebar.contactLists') }}

            <span
              v-if="$currentAccount.contactListsCount"
              class="bg-indigo-500 text-white inline-block px-2 text-xs font-normal rounded-full"
            >
              {{ $currentAccount.contactListsCount }}
            </span>
          </a>
        </RouterLink>
        <RouterLink
          v-if="$membershipHasPermissionTo('read products')"
          :to="{ name: 'accounts.products', params: { accountId: account.id } }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize">{{ $t('account.sidebar.products') }}</a>
        </RouterLink>
        <RouterLink
          v-if="$membershipHasPermissionTo('read forms')"
          :to="{ name: 'accounts.forms', params: { accountId: account.id } }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize">{{ $t('account.sidebar.forms') }}</a>
        </RouterLink>
        <RouterLink
          v-if="$membershipHasPermissionTo('read campaigns')"
          :to="{ name: 'accounts.campaigns', params: { accountId: account.id } }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize">{{ $t('account.sidebar.adPlans') }}</a>
        </RouterLink>
        <RouterLink
          v-if="
            $membershipHasPermissionTo('read orders') &&
            ($currentAccount.type === 'client' || $currentAccount.type === 'organization')
          "
          :to="{ name: 'accounts.orders', params: { accountId: account.id } }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize">{{ $t('account.sidebar.salesOrders') }}</a>
        </RouterLink>

        <RouterLink
          v-if="$membershipHasPermissionTo('read email campaigns')"
          :to="{
            name: 'accounts.accountId.emailCampaigns',
            params: { accountId: account.id },
          }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize">{{ $t('account.sidebar.emailCampaigns') }}</a>
        </RouterLink>
        <RouterLink
          v-if="$membershipHasPermissionTo('read email templates')"
          :to="{
            name: 'accounts.accountId.emailTemplates',
            params: { accountId: account.id },
          }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize">{{ $t('account.sidebar.emailTemplates') }}</a>
        </RouterLink>
        <RouterLink
          v-if="$membershipHasPermissionTo('read sms campaigns')"
          :to="{
            name: 'accounts.accountId.smsCampaigns',
            params: { accountId: account.id },
          }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize">{{ $t('account.sidebar.smsCampaigns') }}</a>
        </RouterLink>
        <RouterLink
          v-if="$membershipHasPermissionTo('read acc products')"
          :to="{
            name: 'accounts.accountId.clientProducts',
            params: { accountId: account.id },
          }"
          tag="li"
          class="nav__item"
        >
          <a class="nav__link capitalize">{{ $t('account.sidebar.myProducts') }}</a>
        </RouterLink>
      </template>
    </ul>
  </div>
</template>

<script>
import { accountComputed } from '@/store/helper';

export default {
  name: 'AccountSidebar',
  computed: {
    ...accountComputed,
  },

  data() {
    return {
      showMore: false,
    };
  },
};
</script>
